import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { CheckIcon, XMarkIcon, LockClosedIcon, ChevronDownIcon, ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { Link } from 'react-router-dom';
import { updateIntegrationProductMapping } from '../../../../libs/fetch.js';
import { toDisplayConsumerIdentifier } from '../../../../libs/gtinUtils.js';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

// Tooltip component for "is" text
function IsTooltip() {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const spanRef = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    const updatePosition = () => {
      if (spanRef.current && isHovered && tooltipRef.current) {
        const rect = spanRef.current.getBoundingClientRect();
        const tooltipHeight = tooltipRef.current.offsetHeight || 200; // Fallback height
        setPosition({
          top: rect.top - tooltipHeight - 8,
          left: rect.left
        });
      }
    };

    if (isHovered) {
      updatePosition();
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);
    }

    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isHovered]);

  return (
    <>
      <span 
        ref={spanRef}
        className="text-gray-600 font-medium whitespace-nowrap cursor-help border-b border-dotted border-gray-400"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        is 
      </span>
      {isHovered && (
        <div 
          ref={tooltipRef}
          className="fixed z-[100] px-3 py-2 text-xs text-white bg-gray-900 rounded shadow-lg whitespace-normal w-80"
          style={{
            position: 'fixed',
            top: `${position.top}px`,
            left: `${position.left}px`
          }}
        >
          <div className="font-semibold mb-2">How values of this product from this source get converted to a Standard Product.</div>
          <div className="space-y-2">
            <div>
              <span className="font-medium">Unmapped:</span> the product has no mapping to a Standard Product and will be ignored in the Unified datasets
            </div>
            <div>
              <span className="font-medium">a Unit:</span> A quantity of 1 will represent 1 Unit of the selected Standard Product
            </div>
            <div>
              <span className="font-medium">a Case/Caddy:</span> A quantity of 1 will represent a case/caddy of the selected Standard Product, and multiplied by the mapped Units per Case
            </div>
            <div>
              <span className="font-medium">a Shipper:</span> A quantity of 1 will represent a shipper Standard Product, which will be split by the underlying products and multiplied by the related Units per Shipper
            </div>
          </div>
          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </>
  );
}

// Custom dropdown with optional search, rendered in a fixed portal menu
function Dropdown({
  value,
  options,
  onChange,
  className = "",
  isChanged = false,
  searchable = false,
  displayValueMap = {},
  disabled = false,
}) {
  const normalizedValue = value == null ? '' : String(value);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    openUpward: false,
    maxHeight: 240
  });

  const selectedOption = options.find(opt => String(opt.value) === normalizedValue);
  const displayLabel = selectedOption?.label || displayValueMap[value] || 'Select...';

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const searchLower = searchTerm.toLowerCase();
    return options.filter((option) =>
      (option.label || '').toLowerCase().includes(searchLower) ||
      (option.caption || '').toLowerCase().includes(searchLower) ||
      (option.productLine || '').toLowerCase().includes(searchLower) ||
      (option.identifier || '').toLowerCase().includes(searchLower)
    );
  }, [options, searchTerm]);

  const updateMenuPosition = useCallback(() => {
    if (!containerRef.current) return;
    const trigger = containerRef.current.querySelector('button');
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const viewportPadding = 12;
    const preferredMenuHeight = searchable ? 320 : 260;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;
    const openUpward = spaceBelow < preferredMenuHeight && spaceAbove > spaceBelow;
    const availableSpace = openUpward ? spaceAbove : spaceBelow;
    const maxHeight = Math.max(120, Math.min(320, availableSpace));

    const maxAllowedWidth = Math.max(220, window.innerWidth - viewportPadding * 2);
    const width = Math.min(rect.width, maxAllowedWidth);
    const left = Math.min(
      Math.max(rect.left, viewportPadding),
      window.innerWidth - width - viewportPadding
    );

    setMenuPosition({
      top: openUpward ? rect.top - 4 : rect.bottom + 4,
      left,
      width,
      openUpward,
      maxHeight
    });
  }, [searchable]);

  useEffect(() => {
    if (!isOpen) return;
    updateMenuPosition();

    const handleOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    const handleWindowChange = () => updateMenuPosition();

    document.addEventListener('mousedown', handleOutside);
    window.addEventListener('resize', handleWindowChange);
    window.addEventListener('scroll', handleWindowChange, true);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      window.removeEventListener('resize', handleWindowChange);
      window.removeEventListener('scroll', handleWindowChange, true);
    };
  }, [isOpen, updateMenuPosition]);

  return (
    <div ref={containerRef} className={classNames("relative", className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          if (!isOpen) {
            updateMenuPosition();
          }
          setIsOpen((prev) => !prev);
        }}
        className={classNames(
          "relative w-full rounded-md px-2 py-1 text-left text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 min-h-[32px]",
          isChanged
            ? "border-yellow-400 bg-yellow-50 border-2"
            : "border border-gray-300 bg-white text-gray-900",
          !selectedOption ? "text-gray-400" : "text-gray-900",
          disabled ? "cursor-not-allowed bg-gray-100 text-gray-400 border-gray-200" : ""
        )}
      >
        <span className="block truncate">{displayLabel}</span>
      </button>
      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
        <ChevronDownIcon className="h-4 w-4 text-gray-400" />
      </span>
      {isOpen && !disabled && createPortal(
        <div
          ref={menuRef}
          className="fixed z-[120] rounded-md bg-white text-sm shadow-lg ring-1 ring-black ring-opacity-5"
          style={{
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
            width: `${menuPosition.width}px`,
            maxWidth: '24rem',
            transform: menuPosition.openUpward ? 'translateY(-100%)' : 'none'
          }}
        >
          {searchable && (
            <div className="border-b border-gray-200 px-3 py-2">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          )}
          <div className="overflow-y-auto py-1" style={{ maxHeight: `${menuPosition.maxHeight}px` }}>
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500 text-center">No results found</div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  disabled={Boolean(option.disabled)}
                  onClick={() => {
                    if (option.disabled || String(option.value) === normalizedValue) {
                      setIsOpen(false);
                      setSearchTerm('');
                      return;
                    }
                    onChange(option.value);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className={classNames(
                    "w-full text-left px-3 py-2",
                    option.disabled
                      ? "text-gray-300 bg-gray-50 cursor-not-allowed"
                      : "text-gray-900 hover:bg-indigo-50",
                    String(option.value) === normalizedValue ? "bg-indigo-50" : ""
                  )}
                >
                  <div className="truncate">{option.label}</div>
                  {option.caption && <div className="text-xs text-gray-500 truncate">{option.caption}</div>}
                </button>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// Static dropdown options - defined outside component to maintain stable references
const IS_A_OPTIONS = [
  { value: 'unmapped', label: 'Unmapped' },
  { value: 'consumer', label: 'a Unit' },
  { value: 'case', label: 'a Case/Caddy' },
  { value: 'shipper', label: 'a Shipper' },
  { value: 'ignored', label: 'Ignored' },
];

const getNormalizedIsA = (product) => {
  if (!product) return 'unmapped';
  if (product.ignore) return 'ignored';
  if (product.is_unmapped) return 'unmapped';
  if (product.represents === 'casepack') return 'case';
  if (
    product.represents === 'consumer' ||
    product.represents === 'case' ||
    product.represents === 'shipper' ||
    product.represents === 'ignored' ||
    product.represents === 'unmapped'
  ) {
    return product.represents;
  }
  return 'unmapped';
};

const initializeEditingState = (product) => {
  if (!product) return { isA: 'unmapped', consumerProductId: null, casepackId: null, shipperId: null };

  const isA = getNormalizedIsA(product);
  const consumerProductId = product.consumer_product_id || null;
  const casepackId = product.shippable_product_id || null;
  const shipperId = product.shippable_product_id || null;

  return {
    isA,
    consumerProductId,
    casepackId,
    shipperId,
  };
};

/** True when draft editing row matches server product (inverse of hasChanges for that row). */
const editingDraftMatchesServerProduct = (draft, product) => {
  if (!draft || !product) return true;
  const normalizedIsA = getNormalizedIsA(product);
  const normalizedConsumerId = product.consumer_product_id || null;
  const normalizedCasepackId = product.shippable_product_id || null;
  const normalizedShipperId = product.shippable_product_id || null;

  return (
    normalizedIsA === draft.isA &&
    normalizedConsumerId === (draft.consumerProductId || null) &&
    normalizedCasepackId === (draft.casepackId || null) &&
    normalizedShipperId === (draft.shipperId || null)
  );
};

export default function IntegrationProductsTable(props) {
  const { products = [], consumerProducts, shippers, currentOrganization, integrationId, onProductsUpdate } = props;
  
  const [showUnmappedOnly, setShowUnmappedOnly] = useState(false);
  const [ignoredFilter, setIgnoredFilter] = useState('show');
  const [searchTerm, setSearchTerm] = useState('');
  const [savingProductKey, setSavingProductKey] = useState(null);
  
  // Helper to get casepack options for a consumer product
  const getCasepackOptions = (consumerProductId) => {
    if (!consumerProductId || !consumerProducts) return [];
    
    const product = consumerProducts.find(p => p.id === consumerProductId);
    if (!product || !product.casepacks || product.casepacks.length === 0) return [];
    
    return product.casepacks.map(casepack => {
      const units = casepack.units_per_casepack || casepack.units_per_case;
      const mainLabel = units ? `Case of ${units}` : 'Case of X';
      
      // Build caption with GTIN above name (if both exist)
      let caption = null;
      if (casepack.gtin && casepack.name) {
        caption = `${casepack.gtin}\n${casepack.name}`;
      } else if (casepack.gtin) {
        caption = casepack.gtin;
      } else if (casepack.name) {
        caption = casepack.name;
      }
      
      return {
        value: casepack.id,
        label: mainLabel,
        caption: caption
      };
    });
  };

  // Initialize editingState with ALL products on mount
  const [editingState, setEditingState] = useState(() => {
    const initial = {};
    products.forEach(product => {
      const productKey = product.product_mapping_key;
      initial[productKey] = initializeEditingState(product);
    });
    return initial;
  });

  const integrationContextRef = useRef(null);

  // Reset all drafts when org or integration changes. When only `products` updates (e.g. after
  // saving one row), merge so other rows keep in-progress edits instead of being wiped.
  useEffect(() => {
    const ctx = `${currentOrganization ?? ''}|${integrationId ?? ''}`;
    const contextChanged = integrationContextRef.current !== ctx;

    if (contextChanged) {
      integrationContextRef.current = ctx;
      const nextState = {};
      products.forEach((product) => {
        nextState[product.product_mapping_key] = initializeEditingState(product);
      });
      setEditingState(nextState);
      setSavingProductKey(null);
      return;
    }

    setEditingState((prev) => {
      const next = {};
      products.forEach((product) => {
        const key = product.product_mapping_key;
        const prevRow = prev[key];
        if (!prevRow) {
          next[key] = initializeEditingState(product);
          return;
        }
        if (!editingDraftMatchesServerProduct(prevRow, product)) {
          next[key] = prevRow;
        } else {
          next[key] = initializeEditingState(product);
        }
      });
      return next;
    });
  }, [products, integrationId, currentOrganization]);

  // Check if a product has been changed from its original/saved state (compare to products prop)
  const hasChanges = (productKey) => {
    const current = editingState[productKey];
    if (!current) return false;
    
    const product = products.find(p => p.product_mapping_key === productKey);
    if (!product) return false;
    
    // Normalize product data to match editing state format
    const normalizedIsA = getNormalizedIsA(product);
    const normalizedConsumerId = product.consumer_product_id || null;
    const normalizedCasepackId = product.shippable_product_id || null;
    const normalizedShipperId = product.shippable_product_id || null;

    return (
      normalizedIsA !== current.isA ||
      normalizedConsumerId !== (current.consumerProductId || null) ||
      normalizedCasepackId !== (current.casepackId || null) ||
      normalizedShipperId !== (current.shipperId || null)
    );
  };

  // Check if all required fields are filled for a product
  const areAllFieldsFilled = (productKey) => {
    const editing = editingState[productKey];
    if (!editing) return false;
    
    const isA = editing.isA;

    if (isA === 'unmapped' || isA === 'ignored') {
      return true; // Unmapped doesn't need any fields
    } else if (isA === 'consumer') {
      return !!editing.consumerProductId;
    } else if (isA === 'case') {
      return !!(editing.consumerProductId && editing.casepackId);
    } else if (isA === 'shipper') {
      return !!editing.shipperId;
    }
    return false;
  };

  // Check if a specific field has been changed
  const hasFieldChanged = (productKey, fieldName) => {
    const current = editingState[productKey];
    if (!current) return false;
    
    const product = products.find(p => p.product_mapping_key === productKey);
    if (!product) return false;
    
    // Map field names and normalize values
    if (fieldName === 'isA') {
      const normalizedIsA = getNormalizedIsA(product);
      return normalizedIsA !== current.isA;
    } else if (fieldName === 'consumerProductId') {
      return (product.consumer_product_id || null) !== (current.consumerProductId || null);
    } else if (fieldName === 'casepackId' || fieldName === 'shipperId') {
      return (product.shippable_product_id || null) !== (current[fieldName] || null);
    }
    
    return false;
  };

  // Revert a product to its original/saved state (from products prop)
  const revertProduct = (productKey) => {
    const product = products.find(p => p.product_mapping_key === productKey);
    if (product) {
      setEditingState(prev => ({
        ...prev,
        [productKey]: initializeEditingState(product)
      }));
    }
  };

  const updateEditingState = useCallback((productKey, updates) => {
    setEditingState(prev => ({
      ...prev,
      [productKey]: {
        ...(prev[productKey] || { isA: 'unmapped', consumerProductId: null, casepackId: null, shipperId: null }),
        ...updates
      }
    }));
  }, []);

  // Save product mapping (with optional useCurrentValues flag to use product's current values instead of editing state)
  const saveProductMapping = async (productKey, useCurrentValues = false) => {
    const product = products.find(p => p.product_mapping_key === productKey);
    
    if (!product || !currentOrganization || !integrationId) {
      console.error('Missing required data for saving');
      return;
    }

    let consumer_product_id, shippable_product_id, represents_shippable, ignore = false;

    if (useCurrentValues) {
      // Use current product values (for lock button)
      consumer_product_id = product.consumer_product_id || null;
      shippable_product_id = product.shippable_product_id || null;
      // Determine represents_shippable from product.represents
      represents_shippable = product.represents === 'case' || product.represents === 'shipper';
      ignore = product.ignore || false;
    } else {
      // Use editing state values (for save button)
      const editing = editingState[productKey];
      if (!editing) {
        console.error('No editing state found for product', productKey);
        return;
      }
      ignore = editing.isA === 'ignored' ? true : false;
      const isA = editing.isA;
      
      // Skip if unmapped
      if (isA === 'unmapped') {
        return;
      }

      // Map isA to the correct parameters
      // For 'consumer': consumer_product_id + default casepack (if exists), represents_shippable = false
      // For 'case': consumer_product_id + casepackId, represents_shippable = true
      // For 'shipper': shipperId only, represents_shippable = true
      consumer_product_id = (isA === 'consumer' || isA === 'case') ? editing.consumerProductId : null;
      
      // For 'consumer', use the default casepack if available (set when product was selected)
      // For 'case', use the selected casepackId
      // For 'shipper', use the shipperId
      if (isA === 'consumer') {
        // Use the default casepack that was set when the product was selected
        shippable_product_id = editing.casepackId || null;
      } else if (isA === 'case') {
        shippable_product_id = editing.casepackId;
      } else if (isA === 'shipper') {
        shippable_product_id = editing.shipperId;
      } else {
        shippable_product_id = null;
      }
      
      represents_shippable = isA === 'case' || isA === 'shipper';
    }

    setSavingProductKey(productKey);

    try {
      const updatedProducts = await updateIntegrationProductMapping(
        currentOrganization,
        integrationId,
        productKey,
        consumer_product_id,
        shippable_product_id,
        represents_shippable,
        ignore
      );

      // Use the response from updateIntegrationProductMapping to update the products
      if (updatedProducts && onProductsUpdate) {
        onProductsUpdate(updatedProducts);
        
        // Update editingState to match the newly saved product
        const savedProduct = updatedProducts.find(p => p.product_mapping_key === productKey);
        if (savedProduct) {
          setEditingState(prev => ({
            ...prev,
            [productKey]: initializeEditingState(savedProduct)
          }));
        }
      }
    } catch (error) {
      console.error('Error saving product mapping:', error);
      alert('Failed to save product mapping. Please try again.');
    } finally {
      setSavingProductKey(null);
    }
  };


  // Options for consumer products dropdown - memoized to prevent recreation
  const consumerProductOptions = useMemo(() => {
    if (!consumerProducts) return [];
    return consumerProducts.map(p => {
      const productId = p.id;
      const identifier = toDisplayConsumerIdentifier(p.ean_upc || p.gtin || '');
      return {
        value: productId,
        label: p.product_name || p.name || 'N/A',
        caption: identifier || null,
        productLine: p.product_line || null,
        identifier: identifier || null
      };
    }).filter(p => p.value !== null);
  }, [consumerProducts]);

  // Options for shippers dropdown - memoized to prevent recreation
  const shipperOptions = useMemo(() => {
    if (!shippers) return [];
    return Object.values(shippers).map(shipper => ({
      value: shipper.id,
      label: shipper.name || 'N/A',
      caption: shipper.gtin || null
    }));
  }, [shippers]);

  const hasNoConsumerProducts = consumerProductOptions.length === 0;
  const hasNoShippers = shipperOptions.length === 0;

  const isAOptions = useMemo(() => {
    return IS_A_OPTIONS.map(option => {
      if (option.value === 'consumer' || option.value === 'case') {
        return { ...option, disabled: hasNoConsumerProducts };
      }
      if (option.value === 'shipper') {
        return { ...option, disabled: hasNoShippers };
      }
      return option;
    });
  }, [hasNoConsumerProducts, hasNoShippers]);

  // Helper to get mapped product name for search
  const getMappedProductName = (product) => {
    const represents = product.represents;
    
    if (represents === 'consumer' && product.consumer_product_id) {
      const consumerProduct = consumerProducts?.find(p => p.id === product.consumer_product_id);
      return consumerProduct?.product_name || consumerProduct?.name || '';
    } else if (represents === 'case' && product.consumer_product_id) {
      const consumerProduct = consumerProducts?.find(p => p.id === product.consumer_product_id);
      if (consumerProduct && product.shippable_product_id) {
        const casepack = consumerProduct.casepacks?.find(cp => cp.id === product.shippable_product_id);
        return casepack?.name || '';
      }
      return consumerProduct?.product_name || consumerProduct?.name || '';
    } else if (represents === 'shipper' && product.shippable_product_id) {
      const shipper = shippers?.[product.shippable_product_id];
      return shipper?.name || '';
    }
    return '';
  };

  // Count unmapped products (including ignored mappings) from saved server state
  const unmappedCount = products.filter(product => {
    const savedIsA = getNormalizedIsA(product);
    return savedIsA === 'unmapped' || savedIsA === 'ignored';
  }).length;

  // Filter products based on saved state + search term.
  // This prevents rows from disappearing/reordering before the user clicks Save.
  const filteredProducts = products.filter(product => {
    const savedIsA = getNormalizedIsA(product);
    const isIgnored = savedIsA === 'ignored';
    const isUnmapped = savedIsA === 'unmapped' || savedIsA === 'ignored';

    // Apply ignored filter first
    if (ignoredFilter === 'hide' && isIgnored) {
      return false;
    }

    // Apply unmapped filter (check product properties directly)
    if (showUnmappedOnly) {
      if (!isUnmapped) return false;
    }

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const mappingKey = (product.product_mapping_key || '').toLowerCase();
      const sourceName = (product.integration_product_name || '').toLowerCase();
      const mappedProductName = getMappedProductName(product).toLowerCase();
      
      return mappingKey.includes(searchLower) || 
             sourceName.includes(searchLower) || 
             mappedProductName.includes(searchLower);
    }

    return true;
  });

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mt-4 flex items-center justify-between gap-4">
        <div className="flex-1 max-w-md">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by mapping key, source name, or mapped product..."
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm whitespace-nowrap text-gray-700">
            <span>Ignored</span>
            <select
              value={ignoredFilter}
              onChange={(e) => setIgnoredFilter(e.target.value)}
              className="px-2 py-1 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
            >
              <option value="show">Show</option>
              <option value="hide">Hide</option>
            </select>
          </label>
          <label className={classNames(
            "flex items-center gap-2 text-sm whitespace-nowrap",
            unmappedCount === 0 
              ? "text-gray-400 cursor-not-allowed" 
              : "text-gray-700 cursor-pointer"
          )}>
            <input
              type="checkbox"
              checked={showUnmappedOnly}
              onChange={(e) => setShowUnmappedOnly(e.target.checked)}
              disabled={unmappedCount === 0}
              className={classNames(
                "h-4 w-4 focus:ring-indigo-500 border-gray-300 rounded",
                unmappedCount === 0 
                  ? "text-gray-300 cursor-not-allowed" 
                  : "text-indigo-600"
              )}
            />
            <span>Show Unmapped Only ({unmappedCount})</span>
          </label>
        </div>
      </div>
      {hasNoConsumerProducts && (
        <div className="mt-4 rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3">
          <div className="flex items-start gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-yellow-800">
              No consumer products found! Product mapping requires consumer products, so
              {' '}
              <Link
                to="/products"
                className="font-medium underline hover:text-yellow-900"
              >
                please create them first.
              </Link>
            </p>
          </div>
        </div>
      )}
      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto overflow-y-visible sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <table className="min-w-full table-fixed" style={{ overflow: 'visible' }}>
              <colgroup>
                <col className="w-44" />
                <col className="w-[32%]" />
                <col />
                <col className="w-32" />
              </colgroup>
              <thead className="bg-white">
                <tr>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3 max-w-[10rem]">
                    <span className="relative group cursor-help border-b border-dotted border-gray-400">
                      Mapping Key
                      <span className="absolute left-0 top-full mt-2 hidden group-hover:block z-50 px-3 py-2 text-xs font-normal text-white bg-gray-900 rounded shadow-lg whitespace-normal w-64">
                        A unique product identifier from the integration's system, usually a UPC, EAN, or other product code.
                        <span className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900"></span>
                      </span>
                    </span>
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Details
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Standard Product
                  </th>
                  <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-3 text-right text-sm font-semibold text-gray-900 w-32">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                  {filteredProducts.map((product, productIdx) => {
                    const productKey = product.product_mapping_key;
                    const editing = editingState[productKey];
                    if (!editing) return null; // Skip if no editing state (shouldn't happen)
                    const currentIsA = editing.isA;
                    const changed = hasChanges(productKey);
                    const hasConsumerOptions = consumerProductOptions.length > 0;
                    const casepackOptions = currentIsA === 'case'
                      ? getCasepackOptions(editing.consumerProductId)
                      : [];
                    const disableCaseOfDropdown = currentIsA === 'case' && !hasConsumerOptions;
                    const disableCaseInDropdown = currentIsA === 'case' && (!editing.consumerProductId || casepackOptions.length === 0);
                    return (
                      <tr
                        key={productKey}
                        className={classNames(
                          productIdx === 0 ? 'border-gray-300' : 'border-gray-200',
                          'border-t',
                          changed ? 'bg-yellow-50 border-yellow-300' : ''
                        )}
                      >
                        <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3 break-words max-w-[10rem]">
                          {productKey}
                        </td>

                        <td className="px-3 py-4 text-sm text-gray-500">
                          {(product.integration_product_name || product.inferred_gtin) ? (
                            <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
                              {product.integration_product_name && (
                                <>
                                  <span className="text-gray-900 font-semibold">Name:</span>
                                  <span>{product.integration_product_name}</span>
                                </>
                              )}
                              {product.inferred_gtin && (
                                <>
                                  <span className="text-gray-900 font-semibold">GTIN/UPC:</span>
                                  <span>{product.inferred_gtin}</span>
                                </>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        
                        <td className="px-3 py-4 text-sm text-gray-500 break-words relative">
                          <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="relative group">
                                <IsTooltip />
                              </div>
                              <div className="min-w-[120px]">
                                <Dropdown
                                  value={currentIsA}
                                  options={isAOptions}
                                  onChange={(value) => {
                                    updateEditingState(productKey, {
                                      isA: value,
                                      consumerProductId: null,
                                      casepackId: null,
                                      shipperId: null
                                    });
                                  }}
                                  isChanged={hasFieldChanged(productKey, 'isA')}
                                />
                              </div>
                              </div>
                              
                              {currentIsA === 'consumer' && (
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-gray-600 font-medium whitespace-nowrap">of </span>
                                  <div className="flex-1 min-w-[200px]">
                                    <Dropdown
                                      value={editing?.consumerProductId}
                                      options={consumerProductOptions}
                                      onChange={(value) => {
                                        // Find the default casepack for the selected consumer product
                                        let defaultCasepackId = null;
                                        if (value && consumerProducts) {
                                          const selectedProduct = consumerProducts.find(p => p.id === value);
                                          if (selectedProduct?.casepacks && selectedProduct.casepacks.length > 0) {
                                            // Look for casepack with is_default flag (could be is_default or is_default_casepack)
                                            const defaultCasepack = selectedProduct.casepacks.find(cp => 
                                              cp.is_default === true || cp.is_default_casepack === true
                                            );
                                            defaultCasepackId = defaultCasepack?.id || null;
                                          }
                                        }
                                        // Set consumer product and default casepack (even though not shown to user)
                                        updateEditingState(productKey, { 
                                          consumerProductId: value,
                                          casepackId: defaultCasepackId
                                        });
                                      }}
                                      isChanged={hasFieldChanged(productKey, 'consumerProductId')}
                                      searchable={true}
                                    />
                                  </div>
                                </div>
                              )}
                              
                              {currentIsA === 'case' && (
                                <>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-gray-600 font-medium whitespace-nowrap">of </span>
                                    <div className="flex-1 min-w-[200px]">
                                      <Dropdown
                                        value={editing.consumerProductId}
                                        options={consumerProductOptions}
                                        onChange={(value) => {
                                          // Find the default casepack for the selected consumer product
                                          let defaultCasepackId = null;
                                          if (value && consumerProducts) {
                                            const selectedProduct = consumerProducts.find(p => {
                                              const pId = p.id;
                                              const searchId = value;
                                              return pId === searchId;
                                            });
                                            if (selectedProduct?.casepacks && selectedProduct.casepacks.length > 0) {
                                              // Look for casepack with is_default flag (could be is_default or is_default_casepack)
                                              const defaultCasepack = selectedProduct.casepacks.find(cp => 
                                                cp.is_default === true || cp.is_default_casepack === true
                                              );
                                              defaultCasepackId = defaultCasepack?.id || null;
                                            }
                                          }
                                          // Set consumer product and default casepack (if exists)
                                          updateEditingState(productKey, { 
                                            consumerProductId: value, 
                                            casepackId: defaultCasepackId 
                                          });
                                        }}
                                        isChanged={hasFieldChanged(productKey, 'consumerProductId')}
                                        searchable={true}
                                        disabled={disableCaseOfDropdown}
                                      />
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-gray-600 font-medium whitespace-nowrap">in </span>
                                    <div className="flex-1 min-w-[200px]">
                                      <Dropdown
                                        value={editing.casepackId}
                                        options={casepackOptions}
                                        onChange={(value) => updateEditingState(productKey, { casepackId: value })}
                                        isChanged={hasFieldChanged(productKey, 'casepackId')}
                                        disabled={disableCaseInDropdown}
                                      />
                                    </div>
                                  </div>
                                </>
                              )}
                              
                              {currentIsA === 'shipper' && (
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-gray-600 font-medium whitespace-nowrap">Shipper:</span>
                                  <div className="flex-1 min-w-[200px]">
                                    <Dropdown
                                      value={editing.shipperId}
                                      options={shipperOptions}
                                      onChange={(value) => updateEditingState(productKey, { shipperId: value })}
                                      isChanged={hasFieldChanged(productKey, 'shipperId')}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 w-32">
                          <div className="flex items-center gap-2 justify-end">
                            {changed && (
                              <>
                                <button
                                  onClick={() => saveProductMapping(productKey)}
                                  disabled={!areAllFieldsFilled(productKey) || savingProductKey === productKey}
                                  className={classNames(
                                    "inline-flex items-center justify-center p-1 text-xs font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2",
                                    areAllFieldsFilled(productKey) && savingProductKey !== productKey
                                      ? "text-primaryBrandColor border border-primaryBrandColor bg-secondaryBrandColor hover:bg-brandPeeledBananaYellow focus:ring-primaryBrandColor"
                                      : "text-gray-400 border border-gray-300 bg-gray-100 cursor-not-allowed"
                                  )}
                                  title={areAllFieldsFilled(productKey) ? "Save changes" : "Please fill in all required fields"}
                                >
                                  <CheckIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => revertProduct(productKey)}
                                  className="inline-flex items-center justify-center p-1 text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 border border-transparent shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                  title="Revert changes"
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                </button>
                              </>
                            )}
                            {!changed && product.represents !== 'unmapped' && product.represents !== 'ignored' && !product.is_explicit_mapping && (
                              <div className="relative group">
                                <button
                                  onClick={() => saveProductMapping(productKey, true)}
                                  disabled={savingProductKey === productKey}
                                  className={classNames(
                                    "inline-flex items-center justify-center p-1 text-xs font-medium rounded-md text-gray-600 border border-gray-300 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500",
                                    savingProductKey === productKey ? "opacity-50 cursor-not-allowed" : ""
                                  )}
                                >
                                  <LockClosedIcon className="h-4 w-4" />
                                </button>
                                <div className="absolute bottom-full right-0 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none whitespace-nowrap z-50">
                                  This mapping is inferred, click to lock-in the mapping
                                  <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

